<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.audit.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <!-- Filter bar -->
        <div class="row align-items-end mb-3 px-4 pt-3">
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.audit.filter_entity_type") }}</label>
            <el-select v-model="filters.entity_type" :placeholder="$t('modern_trade.audit.all')" clearable class="w-100" @change="onFilter">
              <el-option v-for="e in entityTypes" :key="e" :label="entityLabel(e)" :value="e"></el-option>
            </el-select>
          </div>
          <div class="col-md-3">
            <label class="form-control-label">{{ $t("modern_trade.audit.filter_action") }}</label>
            <el-select v-model="filters.action" :placeholder="$t('modern_trade.audit.all')" clearable filterable class="w-100" @change="onFilter">
              <el-option v-for="a in actions" :key="a" :label="actionLabel(a)" :value="a"></el-option>
            </el-select>
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.audit.filter_doc_no") }}</label>
            <input v-model="filters.doc_no" type="text" class="form-control" @keyup.enter="onFilter" />
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.audit.date_from") }}</label>
            <el-date-picker v-model="filters.date_from" type="date" value-format="yyyy-MM-dd" clearable class="w-100" @change="onFilter"></el-date-picker>
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.audit.date_to") }}</label>
            <el-date-picker v-model="filters.date_to" type="date" value-format="yyyy-MM-dd" clearable class="w-100" @change="onFilter"></el-date-picker>
          </div>
        </div>
        <div class="row align-items-end mb-3 px-4">
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.audit.filter_actor") }}</label>
            <input v-model="filters.actor_id" type="number" min="0" class="form-control" @keyup.enter="onFilter" />
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <base-button size="sm" type="primary" @click="onFilter">
              <i class="fa fa-search"></i> {{ $t("modern_trade.audit.search") }}
            </base-button>
            <base-button size="sm" type="secondary" @click="resetFilters">
              {{ $t("modern_trade.audit.reset") }}
            </base-button>
          </div>
        </div>

        <!-- card body = px-0 อยู่แล้ว: ห้ามใส่ negative margin ทับ ไม่งั้นตารางล้นออกนอกกรอบ card ข้างละ 1.5rem -->
        <div v-loading="loading">
          <el-table :data="rows" header-row-class-name="thead-light" size="small" :empty-text="$t('modern_trade.audit.no_data')" style="width: 100%">
            <el-table-column :label="$t('modern_trade.audit.col_time')" min-width="150">
              <template slot-scope="{ row }">{{ formatTime(row.created_at) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.audit.col_actor')" min-width="140">
              <template slot-scope="{ row }">{{ row.actor_name || row.actor_id || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.audit.col_action')" min-width="150">
              <template slot-scope="{ row }">{{ actionLabel(row.action) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.audit.col_entity')" min-width="120">
              <template slot-scope="{ row }">{{ entityLabel(row.entity_type) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.audit.col_doc_no')" min-width="130" prop="doc_no">
              <template slot-scope="{ row }">{{ row.doc_no || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.audit.col_detail')" min-width="110" align="center">
              <template slot-scope="{ row }">
                <base-button size="sm" type="info" @click.native="openDetail(row)">
                  <i class="fa fa-eye"></i> {{ $t("modern_trade.audit.view_detail") }}
                </base-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="d-flex align-items-center justify-content-between mt-3 px-4">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
          <base-pagination v-model="page" :per-page="perPage" :total="total" @input="loadLogs"></base-pagination>
        </div>
      </card>
    </div>

    <!-- Diff dialog (before/after computed on the FE) -->
    <el-dialog :title="$t('modern_trade.audit.diff_title')" :visible.sync="showDetail" width="720px" append-to-body>
      <div v-if="detailRow">
        <div class="mb-2">
          <strong>{{ actionLabel(detailRow.action) }}</strong>
          <span class="text-muted ml-2">{{ entityLabel(detailRow.entity_type) }}</span>
          <span v-if="detailRow.doc_no" class="text-muted ml-2">· {{ detailRow.doc_no }}</span>
        </div>
        <div class="text-muted small mb-3">
          {{ formatTime(detailRow.created_at) }} · {{ detailRow.actor_name || detailRow.actor_id || "-" }}
        </div>
        <p v-if="detailRow.note" class="mb-2">{{ detailRow.note }}</p>

        <el-table :data="diffRows" header-row-class-name="thead-light" size="small" :empty-text="$t('modern_trade.audit.no_change')" style="width: 100%">
          <el-table-column :label="$t('modern_trade.audit.diff_field')" min-width="160" prop="key"></el-table-column>
          <el-table-column :label="$t('modern_trade.audit.diff_old')" min-width="220">
            <template slot-scope="{ row }">
              <span :class="{ 'audit-changed-old': row.changed }">{{ row.old }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.audit.diff_new')" min-width="220">
            <template slot-scope="{ row }">
              <span :class="{ 'audit-changed-new': row.changed }">{{ row.new }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer">
        <el-button @click="showDetail = false">{{ $t("close") }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Dialog, Button, DatePicker } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import swal from "sweetalert2";
import { ModernTradeService } from "@/resource";

// The full set of MT write actions the BE records (spec §3). Kept in sync with
// the action strings emitted by MtAuditLog(...) hooks.
const ACTIONS = [
  "so.create", "so.update", "so.confirm", "so.cancel", "so.close",
  "delivery.create", "delivery.ship_status",
  "invoice.create", "invoice.cancel",
  // payment.link / payment.unlink — ฝากขาย (spec 2026-08-13): ผูก/ปลดเงินรับกับ
  // ใบกำกับ ทั้งที่ผู้ใช้ทำเองที่หน้า "รับเงินฝากขาย" และที่ระบบ auto-link ตอนออกใบ
  "payment.create", "payment.link", "payment.unlink", "payment.delete",
  "product.create", "product.update", "product.delete", "product.import",
  "price_template.update", "price_template.items",
  "company.update", "setting.update",
];
const ENTITY_TYPES = [
  "sale_order", "invoice", "payment", "delivery",
  "product", "price_template", "company", "setting",
];

export default {
  name: "MtAuditLog",
  components: {
    RouteBreadCrumb,
    BasePagination,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [DatePicker.name]: DatePicker,
  },
  data() {
    return {
      loading: false,
      rows: [],
      total: 0,
      page: 1,
      perPage: 20,
      actions: ACTIONS,
      entityTypes: ENTITY_TYPES,
      filters: {
        entity_type: "",
        action: "",
        actor_id: "",
        doc_no: "",
        date_from: "",
        date_to: "",
      },
      showDetail: false,
      detailRow: null,
    };
  },
  computed: {
    // Field-level diff of before_json vs after_json, computed entirely on the FE
    // (the BE stores the two raw snapshots and never pre-diffs).
    diffRows() {
      if (!this.detailRow) return [];
      const before = this.parseJson(this.detailRow.before_json);
      const after = this.parseJson(this.detailRow.after_json);
      const beforeObj = before && typeof before === "object" ? before : {};
      const afterObj = after && typeof after === "object" ? after : {};
      const keys = Array.from(new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)])).sort();
      const created = before === null || before === undefined;
      const deleted = after === null || after === undefined;
      return keys.map((k) => {
        const oldV = this.fmtValue(beforeObj[k]);
        const newV = this.fmtValue(afterObj[k]);
        return {
          key: k,
          old: created ? "" : oldV,
          new: deleted ? "" : newV,
          changed: created || deleted || oldV !== newV,
        };
      });
    },
  },
  mounted() {
    this.loadLogs();
  },
  methods: {
    async loadLogs() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.filters.entity_type) params.entity_type = this.filters.entity_type;
        if (this.filters.action) params.action = this.filters.action;
        if (this.filters.actor_id) params.actor_id = this.filters.actor_id;
        if (this.filters.doc_no) params.doc_no = this.filters.doc_no.trim();
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
        const res = await ModernTradeService.auditLogs(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.rows = d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    onFilter() {
      this.page = 1;
      this.loadLogs();
    },
    resetFilters() {
      this.filters = { entity_type: "", action: "", actor_id: "", doc_no: "", date_from: "", date_to: "" };
      this.page = 1;
      this.loadLogs();
    },
    openDetail(row) {
      this.detailRow = row;
      this.showDetail = true;
    },
    parseJson(s) {
      if (s === null || s === undefined || s === "") return null;
      if (typeof s === "object") return s;
      try {
        return JSON.parse(s);
      } catch (e) {
        return null;
      }
    },
    fmtValue(v) {
      if (v === null || v === undefined || v === "") return this.$t("modern_trade.audit.empty_value");
      if (typeof v === "object") return JSON.stringify(v);
      return String(v);
    },
    actionLabel(action) {
      if (!action) return "-";
      const key = "modern_trade.audit.action_" + action.replace(/\./g, "_");
      const t = this.$t(key);
      return t === key ? action : t;
    },
    entityLabel(entityType) {
      if (!entityType) return "-";
      const key = "modern_trade.audit.entity_" + entityType;
      const t = this.$t(key);
      return t === key ? entityType : t;
    },
    formatTime(v) {
      if (!v) return "-";
      const dt = new Date(v);
      if (isNaN(dt.getTime())) return v;
      const p = (n) => String(n).padStart(2, "0");
      return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
    },
    errMsg(err) {
      return (err && err.response && err.response.data && (err.response.data.Msg || err.response.data.message)) || (err && err.message) || "Error";
    },
  },
};
</script>

<style scoped>
.audit-changed-old {
  background: #fdecec;
  color: #c0392b;
  padding: 1px 4px;
  border-radius: 3px;
}
.audit-changed-new {
  background: #e3f6e8;
  color: #1e7d43;
  padding: 1px 4px;
  border-radius: 3px;
}
</style>
